import React from 'react';
import { motion } from 'motion/react';
import { Navigate, Link } from 'react-router-dom';
import { 
  Zap, 
  MessageSquare, 
  ShieldCheck, 
  Database, 
  Globe, 
  Scale, 
  Vote, 
  CreditCard,
  CheckCircle2,
  Lock
} from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { usePermissions } from '../hooks/usePermissions';
import { useTenant } from '../contexts/TenantContext';
import { useTogglePowerUp, useEnabledModules } from '../hooks/useModules';
import { toast } from 'react-hot-toast';

const PowerupCard = ({ title, desc, price, icon: Icon, type, active = false, powerUpKey, onToggle, isPending = false, disabled = false }: any) => {
  return (
  <motion.div 
    whileHover={{ y: -5 }}
    className={`bg-white dark:bg-black rounded-[2rem] border-2 p-6 flex flex-col h-full transition-all ${
      active ? 'border-brand-primary ring-1 ring-brand-primary/20' : 'border-black dark:border-white'
    }`}
  >
    <div className="flex items-start justify-between mb-4">
      <div className={`w-12 h-12 rounded-none flex items-center justify-center ${active ? 'bg-brand-primary text-white' : 'bg-white text-gray-400 dark:bg-slate-950'}`}>
        <Icon size={24} />
      </div>
      <Badge variant={active ? 'blue' : 'gray'} className="text-[9px] uppercase font-bold tracking-widest">
        {type}
      </Badge>
    </div>
    
    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">{title}</h3>
    <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-6 flex-grow">{desc}</p>
    
    <div className="flex items-center justify-between mt-auto pt-6 border-t border-gray-50">
      <div className="flex flex-col">
        <span className="text-[10px] uppercase font-bold text-gray-300 tracking-widest font-mono">Cost</span>
        <span className="text-lg font-black text-gray-900 dark:text-white">{price}</span>
      </div>
      <button 
        onClick={() => onToggle(powerUpKey, !active)} 
        disabled={isPending || disabled}
        className={`px-4 py-2 rounded-none text-xs font-bold uppercase tracking-widest transition-all disabled:opacity-50 ${
        active 
          ? 'bg-red-50 text-red-600 hover:bg-red-100 cursor-pointer' 
          : 'bg-navy-900 text-white hover:bg-navy-800 shadow-[4px_4px_0px_#000000] dark:shadow-[4px_4px_0px_#FFFFFF] shadow-navy-900/10 cursor-pointer'
      }`}>
        {isPending ? 'Updating...' : active ? 'Disable' : 'Unleash'}
      </button>
    </div>
  </motion.div>
  );
};

export const PowerupsPage = () => {
  const { isAdmin } = usePermissions();
  const { isUniversal, isGlobal, node } = useTenant();
  const toggleMutation = useTogglePowerUp(node?.id || '');
  const activeModules = useEnabledModules();
  const [togglingKey, setTogglingKey] = React.useState<string | null>(null);

  if (!isAdmin && !isUniversal) {
    return <Navigate to="/dashboard" replace />;
  }

  const isContextMissing = !node?.id || node.id === 'platform-root' || isUniversal || isGlobal;

  const handleToggle = (powerUpKey: string, enable: boolean) => {
    if (isContextMissing) {
      toast.error('Select a specific organization from the Org Switcher to manage its powerups.');
      return;
    }
    setTogglingKey(powerUpKey);
    toggleMutation.mutate(
      { powerUp: powerUpKey, enable },
      { onSettled: () => setTogglingKey(null) }
    );
  };

  return (
    <div className="max-w-7xl mx-auto py-12 px-6">
      <header className="mb-12 text-center max-w-2xl mx-auto">
        <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-600 px-3 py-1 rounded-none mb-6 border-2 border-black dark:border-white border-amber-100">
          <Zap size={14} fill="currentColor" />
          <span className="text-[10px] font-bold uppercase tracking-widest">Enhance Your Roster</span>
        </div>
        <h1 className="text-4xl font-black text-gray-900 dark:text-gray-100 tracking-tight leading-none mb-4">
          Org Powerups
        </h1>
        <p className="text-gray-500 text-sm leading-relaxed">
          Modular enhancements for organizations that need specific tools without the overhead of a full enterprise subscription.
        </p>
        {isContextMissing && (
          <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-950/30 border-2 border-black dark:border-white text-amber-800 dark:text-amber-300 text-xs font-bold">
            Select a specific organization using the Switch Org menu in the top bar to enable or disable powerups.
          </div>
        )}
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        <PowerupCard 
          title="SMS Mega-Bundle"
          desc="Perfect for emergency alerts. Adds a block of 500 high-priority SMS notifications for your Pulse updates."
          price="$19"
          type="One-Time"
          icon={MessageSquare}
          powerUpKey="SMS_BUNDLE"
          active={!isContextMissing && activeModules.includes('SMS_BUNDLE')}
          isPending={togglingKey === 'SMS_BUNDLE' && toggleMutation.isPending}
          onToggle={handleToggle}
        />
        <PowerupCard 
          title="Passport Vetting"
          desc="Pre-paid block of 10 background checks for new members. Integrated directly with our identity providers."
          price="$49"
          type="Credit Pack"
          icon={ShieldCheck}
          powerUpKey="PASSPORT_VETTING"
          active={!isContextMissing && activeModules.includes('PASSPORT_VETTING')}
          isPending={togglingKey === 'PASSPORT_VETTING' && toggleMutation.isPending}
          onToggle={handleToggle}
        />
        <PowerupCard 
          title="Vault Expansion"
          desc="Need more room for blueprints or compliance docs? Adds +25GB of encrypted storage to your organization."
          price="$5/mo"
          type="Storage"
          icon={Database}
          powerUpKey="VAULT_EXPANSION"
          active={!isContextMissing && activeModules.includes('VAULT_EXPANSION')}
          isPending={togglingKey === 'VAULT_EXPANSION' && toggleMutation.isPending}
          onToggle={handleToggle}
        />
        <PowerupCard 
          title="Election Guard"
          desc="Run secure, anonymized board elections with cryptographic verification and external audit logs."
          price="$10/event"
          type="Governance"
          icon={Vote}
          powerUpKey="ELECTIONS"
          active={!isContextMissing && activeModules.includes('ELECTIONS')}
          isPending={togglingKey === 'ELECTIONS' && toggleMutation.isPending}
          onToggle={handleToggle}
        />
        <PowerupCard 
          title="Legal Blueprint"
          desc="Access a library of legally vetted bylaws, grievance forms, and conflict resolution workflows."
          price="$15/mo"
          type="Compliance"
          icon={Scale}
          powerUpKey="LEGAL_DOCS"
          active={!isContextMissing && activeModules.includes('LEGAL_DOCS')}
          isPending={togglingKey === 'LEGAL_DOCS' && toggleMutation.isPending}
          onToggle={handleToggle}
        />
      </div>

      <div className="mt-16 bg-navy-900 rounded-[3rem] p-12 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 p-12 opacity-10">
          <Globe size={240} />
        </div>
        <div className="relative z-10 max-w-xl">
          <Badge variant="blue" className="bg-brand-primary text-white border-none mb-6">Enterprise Bonus</Badge>
          <h2 className="text-3xl font-black mb-4">Unite Multiple Orgs?</h2>
          <p className="text-navy-300 text-sm leading-relaxed mb-8">
            Network accounts include all Powerups across every child node in the hierarchy. If you manage more than 5 organizations, switching to Network billing saves up to 40%.
          </p>
          <div className="flex gap-4">
            <Link to="/plans" className="px-8 py-3 bg-brand-secondary text-navy-900 dark:text-white rounded-none font-bold text-xs uppercase tracking-widest hover:bg-white transition-all inline-block text-center dark:bg-slate-900">
              Compare Tiers
            </Link>
            <Link to="/contact" className="px-8 py-3 border-2 border-black dark:border-white border-navy-700 rounded-none font-bold text-xs uppercase tracking-widest hover:bg-navy-800 transition-all flex items-center gap-2 justify-center text-white">
              <Lock size={14} /> Request Quote
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};
